import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Button, Money, SkeletonRows, Status } from "../../../../components/ui";
import { focusRing } from "../../../../components/ui/cn";
import { businessApi } from "../../../../services/api";
import { formatPayDate } from "../compensationView";

// My HR > Pay (APP-HR-065): the signed-in employee's own standing deductions and loans. Read-only: the server
// resolves the employee from the session, and nothing here can be edited. A loan's balance moves only when a salary
// is confirmed as received, so "paid", "still owed" and "taken, waiting for your confirmation" are three figures.

const panel = "rounded-panel border border-default bg-surface";

const FREQUENCY = {
  per_payroll: "every payroll",
  semi_monthly: "twice a month",
  monthly: "once a month, on the last payroll of the month",
};

const STATE = {
  active: { label: "Active", tone: "neutral" },
  upcoming: { label: "Not started", tone: "neutral" },
  paused: { label: "Paused", tone: "warning" },
  ended: { label: "Ended", tone: "neutral" },
  completed: { label: "Fully paid", tone: "success" },
};

export const isCurrent = (item) => ["active", "upcoming", "paused"].includes(item.state);

/** "₱1,000.00 every payroll" / "2.5% of gross pay, once a month…" / "Not taken by payroll". */
function Taken({ item }) {
  if (!item.takenByPayroll) return <>Not taken by payroll</>;
  const how = FREQUENCY[item.frequency] || "each time it applies";
  if (item.ratePercent != null) return <>{Number(item.ratePercent)}% of gross pay, {how}</>;
  if (item.amountCents != null) return <><Money value={item.amountCents} className="font-medium text-foreground" /> {how}</>;
  return <>Taken {how}</>;
}

function Dates({ item }) {
  if (item.state === "upcoming") return <>Starts {formatPayDate(item.effectiveFrom)}</>;
  if (item.effectiveUntil) return <>{formatPayDate(item.effectiveFrom)} – {formatPayDate(item.effectiveUntil)}</>;
  return <>Since {formatPayDate(item.effectiveFrom)}</>;
}

function LoanFigures({ loan }) {
  return (
    <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-subtle pt-3 sm:grid-cols-4">
      <div className="min-w-0">
        <dt className="text-meta text-secondary">Loan amount</dt>
        <dd className="mt-0.5 text-body-sm font-medium text-foreground"><Money value={loan.amountCents} /></dd>
      </div>
      <div className="min-w-0">
        <dt className="text-meta text-secondary">Paid so far</dt>
        <dd className="mt-0.5 text-body-sm font-medium text-foreground"><Money value={loan.paidCents} /></dd>
      </div>
      <div className="min-w-0">
        <dt className="text-meta text-secondary">Still owed</dt>
        <dd className="mt-0.5 text-body-sm font-semibold text-foreground"><Money value={loan.remainingCents} /></dd>
      </div>
      <div className="min-w-0">
        <dt className="text-meta text-secondary">Each installment</dt>
        <dd className="mt-0.5 text-body-sm font-medium text-foreground">{loan.installmentCents != null ? <Money value={loan.installmentCents} /> : "Not set"}</dd>
      </div>
    </dl>
  );
}

function Item({ item }) {
  const state = STATE[item.state] || STATE.active;
  return (
    <li className="px-4 py-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="text-body-sm font-medium text-foreground [overflow-wrap:anywhere]">{item.name}</p>
          <p className="mt-0.5 text-body-sm text-secondary"><Taken item={item} /></p>
          <p className="mt-0.5 text-meta text-secondary"><Dates item={item} /></p>
        </div>
        <Status tone={state.tone} icon={state.tone !== "neutral"} className={`shrink-0 text-body-sm ${state.tone === "neutral" ? "text-secondary" : "font-medium"}`}>{state.label}</Status>
      </div>
      {item.loan ? <LoanFigures loan={item.loan} /> : null}
      {item.loan && item.loan.awaitingConfirmationCents > 0 ? (
        <p className="mt-2 text-body-sm text-secondary">
          <Money value={item.loan.awaitingConfirmationCents} className="font-medium text-foreground" /> was taken on a released payslip and is not counted as
          paid yet. It comes off what you owe when you confirm receiving that salary.{" "}
          <Link to="/business/hr/my-payslips" className={`rounded-control font-medium text-accent hover:underline ${focusRing}`}>Open My Payslips</Link>
        </p>
      ) : null}
    </li>
  );
}

export default function MyDeductionsPanel({ reloadKey = 0 }) {
  const [state, setState] = useState({ loading: true, error: "", items: [] });
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await businessApi.getMyDeductions();
      setState({ loading: false, error: "", items: Array.isArray(data?.items) ? data.items : [] });
    } catch (err) {
      setState({ loading: false, error: err?.serverMessage || "Your deductions could not be loaded.", items: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const { loading, error, items } = state;
  const current = items.filter(isCurrent);
  const past = items.filter((item) => !isCurrent(item));

  return (
    <section aria-label="My deductions and loans">
      <h2 className="text-section-title text-foreground">My deductions and loans</h2>
      <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">
        What your employer takes from your pay on a standing basis, and what you still owe on a loan. HR sets and changes these.
      </p>
      <div className="mt-3 space-y-3">
        {loading ? (
          <div className={panel}><SkeletonRows rows={2} columns={2} label="Loading your deductions" /></div>
        ) : error ? (
          <div role="alert" className={`${panel} flex flex-col items-start gap-3 p-4 text-body-sm text-foreground sm:flex-row sm:items-center sm:justify-between`}>
            <span>{error}</span>
            <Button size="sm" onClick={load}>Try again</Button>
          </div>
        ) : (
          <>
            {current.length ? (
              <ul className={`${panel} divide-y divide-subtle`} aria-label="Current deductions and loans">
                {current.map((item) => <Item key={item.id} item={item} />)}
              </ul>
            ) : (
              <div className={`${panel} p-4 text-body-sm text-secondary`}>
                <p className="text-panel-title text-foreground">You have no standing deduction or loan.</p>
                <p className="mt-1">If you expected to see one here, ask your HR contact.</p>
              </div>
            )}
            {past.length ? (
              <div>
                <Button size="sm" variant="quiet" aria-expanded={showPast} onClick={() => setShowPast((open) => !open)}>
                  {showPast ? "Hide" : "Show"} {past.length} ended or fully paid
                </Button>
                {showPast ? (
                  <ul className={`${panel} mt-2 divide-y divide-subtle`} aria-label="Ended and fully paid">
                    {past.map((item) => <Item key={item.id} item={item} />)}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <p className="max-w-[75ch] text-body-sm text-secondary">
              SSS, PhilHealth, Pag-IBIG and withholding tax are not listed here: they are worked out on each payslip. What was actually taken in a
              period is on that payslip.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
